import React,{useEffect,useState} from 'react';
import {createPortal} from 'react-dom';
import {motion} from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const {FiX,FiSave,FiImage,FiTrash2}=FiIcons;

const EVENT_TYPES=[
  {id: 'examen',label: 'Examen'},
  {id: 'torneo',label: 'Torneo'},
  {id: 'convivio',label: 'Convivio'},
  {id: 'clase-especial',label: 'Clase especial'},
];

// Las imágenes se reducen antes de guardarse: la base de datos admite
// hasta 2 MB por registro y así los eventos cargan rápido en el celular.
const MAX_IMAGE_SIDE=1600;
const MAX_IMAGE_LENGTH=1400000;

const compressImage=(file)=> new Promise((resolve,reject)=>{
  const url=URL.createObjectURL(file);
  const img=new Image();
  img.onload=()=>{
    URL.revokeObjectURL(url);
    const scale=Math.min(1,MAX_IMAGE_SIDE/Math.max(img.width,img.height));
    const canvas=document.createElement('canvas');
    canvas.width=Math.round(img.width*scale);
    canvas.height=Math.round(img.height*scale);
    const ctx=canvas.getContext('2d');
    ctx.fillStyle='#fff';
    ctx.fillRect(0,0,canvas.width,canvas.height);
    ctx.drawImage(img,0,0,canvas.width,canvas.height);
    for (const quality of [0.85,0.7,0.55,0.4]) {
      const dataUrl=canvas.toDataURL('image/jpeg',quality);
      if (dataUrl.length<=MAX_IMAGE_LENGTH) return resolve(dataUrl);
    }
    reject(new Error('No se pudo reducir la imagen lo suficiente.'));
  };
  img.onerror=()=>{
    URL.revokeObjectURL(url);
    reject(new Error('No se pudo leer la imagen.'));
  };
  img.src=url;
});

const EMPTY_EVENT={
  title: '',
  type: 'examen',
  eventDate: '',
  time: '',
  place: '',
  mapUrl: '',
  description: '',
  image: '',
  status: true,
};

export default function EventForm({event,user,onClose,onSave}) {
  const [formData,setFormData]=useState(event || EMPTY_EVENT);
  const [imageError,setImageError]=useState('');

  useEffect(()=>{
    document.body.style.overflow='hidden';

    return ()=>{
      document.body.style.overflow='unset';
    };
  },[]);

  const update=(field,value)=>{
    setFormData({...formData,[field]: value});
  };

  const handleImageChange=(e)=>{
    const file=e.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setImageError('Selecciona un archivo de imagen válido.');
      return;
    }

    if (file.size>15*1024*1024) {
      setImageError('La imagen debe pesar menos de 15 MB.');
      return;
    }

    setImageError('');

    compressImage(file)
      .then(dataUrl=> setFormData(current=> ({...current,image: dataUrl})))
      .catch(error=> setImageError(error.message));
  };

  const submit=(e)=>{
    e.preventDefault();

    onSave({
      ...formData,
      registeredBy: event?.registeredBy || user?.name || 'Administrador',
      registeredAt: event?.registeredAt || new Date().toISOString(),
    });
  };

  const modal=(
    <div className="fixed inset-0 z-[99999] flex items-start justify-center overflow-y-auto bg-zinc-950/95 backdrop-blur-xl py-10 px-4">
      <button
        type="button"
        aria-label="Cerrar ventana"
        className="fixed inset-0 cursor-default"
        onClick={onClose}
      />

      <motion.div
        initial={{opacity: 0,y: 20,scale: .96}}
        animate={{opacity: 1,y: 0,scale: 1}}
        className="relative z-[100000] glass-modal w-full max-w-3xl rounded-[2.5rem] overflow-hidden"
        onClick={e=> e.stopPropagation()}
      >
        <div className="p-8 border-b border-white/5 flex items-center justify-between bg-white/5">
          <div>
            <span className="text-red-500 text-[10px] font-black uppercase tracking-[.3em]">
              Administración
            </span>

            <h2 className="text-3xl font-black uppercase tracking-tighter text-white mt-2">
              {event ? 'Editar evento' : 'Nuevo evento'}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-3 rounded-xl bg-white/5 hover:bg-red-600 text-white"
          >
            <SafeIcon icon={FiX} />
          </button>
        </div>

        <form
          onSubmit={submit}
          className="relative z-10 p-8 space-y-6"
        >
          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Título *">
              <input
                required
                value={formData.title}
                onChange={e=> update('title',e.target.value)}
                className="event-input"
              />
            </Field>

            <Field label="Tipo *">
              <select
                value={formData.type}
                onChange={e=> update('type',e.target.value)}
                className="event-input"
              >
                {EVENT_TYPES.map(type=> (
                  <option key={type.id} value={type.id}>
                    {type.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Fecha del evento *">
              <input
                required
                type="date"
                value={formData.eventDate}
                onChange={e=> update('eventDate',e.target.value)}
                className="event-input"
              />
            </Field>

            <Field label="Hora *">
              <input
                required
                type="time"
                value={formData.time}
                onChange={e=> update('time',e.target.value)}
                className="event-input"
              />
            </Field>

            <Field label="Lugar *">
              <input
                required
                value={formData.place}
                onChange={e=> update('place',e.target.value)}
                className="event-input"
              />
            </Field>

            <label className="flex items-center gap-3 self-end h-14 cursor-pointer text-white text-xs font-black uppercase tracking-widest">
              <input
                type="checkbox"
                checked={formData.status}
                onChange={e=> update('status',e.target.checked)}
                className="accent-red-600 w-5 h-5"
              />
              Evento activo
            </label>
          </div>

          <Field label="Mapa (opcional)">
            <textarea
              rows="2"
              value={formData.mapUrl || ''}
              onChange={e=> update('mapUrl',e.target.value)}
              placeholder="Coordenadas del lugar, por ejemplo: 21.1234, -101.6789"
              className="event-input resize-none"
            />
            <p className="mt-2 ml-2 text-xs text-white/70">
              En Google Maps, haz clic derecho sobre el lugar y toca los números que aparecen para copiar las coordenadas. También sirve pegar el código de Compartir → Incorporar un mapa. Si lo dejas vacío, el mapa se busca con el campo Lugar, que a veces no ubica bien la dirección.
            </p>
          </Field>

          <Field label="Descripción *">
            <textarea
              required
              rows="5"
              value={formData.description}
              onChange={e=> update('description',e.target.value)}
              className="event-input resize-none"
            />
          </Field>

          <Field label="Imagen del evento">
            <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 p-4">
              <label className="flex cursor-pointer items-center justify-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-4 text-[10px] font-black uppercase tracking-widest text-white hover:bg-white/10">
                <SafeIcon icon={FiImage} className="text-red-500" />
                Seleccionar imagen

                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleImageChange}
                  className="hidden"
                />
              </label>

              <p className="mt-3 text-center text-[10px] uppercase tracking-wider text-white/40">
                PNG, JPG o WEBP · máximo 15 MB
              </p>

              {imageError && (
                <p className="mt-3 text-center text-xs font-bold text-red-400">
                  {imageError}
                </p>
              )}

              {formData.image && (
                <div className="relative mt-4 overflow-hidden rounded-2xl border border-white/10">
                  <img
                    src={formData.image}
                    alt="Vista previa del evento"
                    className="h-48 w-full object-cover"
                  />

                  <button
                    type="button"
                    onClick={()=> update('image','')}
                    className="absolute right-3 top-3 rounded-xl bg-black/70 p-3 text-white hover:bg-red-600"
                    aria-label="Eliminar imagen"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>
              )}
            </div>
          </Field>

          <div className="flex flex-col sm:flex-row gap-4 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-4 rounded-2xl bg-white/5 hover:bg-white/10 text-white text-[10px] font-black uppercase tracking-widest"
            >
              Cancelar
            </button>

            <button
              type="submit"
              className="relative z-20 flex-[2] py-4 rounded-2xl bg-red-600 hover:bg-red-500 text-white text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-2 cursor-pointer"
            >
              <SafeIcon icon={FiSave} />
              Guardar evento
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );

  return createPortal(modal,document.body);
}

function Field({label,children}) {
  return (
    <div className="space-y-2">
      <label className="text-[10px] font-black uppercase tracking-widest text-zinc-400 ml-2">
        {label}
      </label>

      {children}
    </div>
  );
}